import { useTranslation } from '@pypestream/translations';
import { useEffect, useRef } from 'react';
import { proStudioUrqlGql } from '@pypestream/api-services';
import { dismissToasts } from '@pypestream/design-system';

import { tableStore } from '../stores/table-store';
import { resolveBobcatAccess } from '../utils/bobcat-flags';
import { publishToastCopy } from '../components/modals/publish-modal/publish-toast-copy';
import {
  isPublishFailed,
  notifyPublishComplete,
  PUBLISH_IN_PROGRESS_TOAST_ID,
  type SolutionVersionStatus,
} from '../utils/watch-publish-status';
import { sendUserMessage } from '../xstate/app.xstate-utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';

/**
 * Subscribes to tenancy.solution_versions (status). When status becomes
 * PUBLISHING, switches to view mode and shows toast. When status becomes
 * ACTIVE again, dismisses the toast and switches back to edit mode.
 * Runs on any page where a draft solution version is loaded (workflows,
 * actions, files, channels, versions, etc.), not only the workflows table.
 */
export function usePublishStatusWatcherForCoworkers(): void {
  const [t] = useTranslation();
  const solutionVersionId = useProStudioCtxSelector(
    (ctx) => ctx.draftVersionSolutionData?.id ?? null
  );

  const publishInProgressForVersionIdRef = useRef<string | null>(null);

  // PSTUDIO-3985: `t` is read through a ref so it can stay OUT of the effect's
  // dependency array. `useTranslation` is re-exported straight from
  // react-i18next, whose `t` identity changes on `languageChanged`/`loaded`/
  // `added` — and pro-studio lazy-loads scoped namespaces, so one landing
  // mid-publish used to re-run this effect for an UNCHANGED solution version.
  // That was harmless while the cleanup only flipped a boolean; now that it also
  // dismisses the sticky banner and restores edit mode, the same event would
  // drop a user back into editing on a solution that is actively publishing,
  // until the resubscribe replayed PUBLISHING and yanked them back.
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (!solutionVersionId) {
      publishInProgressForVersionIdRef.current = null;
      return;
    }

    const { subscribe } = proStudioUrqlGql.solutionVersionStatusSubscription({
      id: solutionVersionId,
    });
    const { unsubscribe } = subscribe((subData) => {
      if (subData.error) return;
      const row = subData.data?.tenancy_solution_versions_by_pk;
      if (row == null) return;
      const status = row.status as SolutionVersionStatus | undefined;

      if (status === 'PUBLISHING') {
        if (publishInProgressForVersionIdRef.current !== solutionVersionId) {
          publishInProgressForVersionIdRef.current = solutionVersionId;
          // This user's own publish already raised the lock and its toast
          // (`PublishModal`, auto-publish on create); a second one would stack.
          const alreadyAnnounced =
            tableStore.solutionVersionPublishingInProgress;
          tableStore.setSolutionVersionPublishingInProgress(true);
          tableStore.setState('viewing');
          if (!alreadyAnnounced) {
            const copy = publishToastCopy(
              resolveBobcatAccess('fiveStageNav', tableStore.featureFlags) ===
                'allowed'
            );
            sendUserMessage({
              type: 'info',
              header:
                tRef.current(copy.header.key, {
                  defaultValue: copy.header.defaultValue,
                }) ?? undefined,
              text:
                tRef.current(copy.text.key, {
                  defaultValue: copy.text.defaultValue,
                }) ?? '',
              toastId: PUBLISH_IN_PROGRESS_TOAST_ID,
              autoClose: false,
            });
          }
        }
      } else if (
        status === 'ACTIVE' &&
        publishInProgressForVersionIdRef.current === solutionVersionId
      ) {
        publishInProgressForVersionIdRef.current = null;
        // A failed publish reverts PUBLISHING -> ACTIVE (there is no FAILED
        // status). Don't announce success or reload — just restore UI state.
        if (isPublishFailed(solutionVersionId)) {
          tableStore.setSolutionVersionPublishingInProgress(false);
          dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
          // PSTUDIO-3985: `isViewer`-aware for the same reason as the cleanup
          // below and `restorePublishUiState` — a failed publish must not hand
          // a read-only user an editable table.
          tableStore.setState(tableStore.isViewer ? 'viewing' : 'editing');
          return;
        }
        const { success } = publishToastCopy(
          resolveBobcatAccess('fiveStageNav', tableStore.featureFlags) ===
            'allowed'
        );
        const successMessage =
          tRef.current(success.key, {
            defaultValue: success.defaultValue,
          }) ?? '';
        notifyPublishComplete(solutionVersionId, successMessage);
        sendProStudioEvent({ type: 'reloadCurrentBotData' });
      }
    });

    return () => {
      unsubscribe();
      // PSTUDIO-3985: undo everything the PUBLISHING branch did, not just the
      // flag. All three of its effects are global (the `tableStore` singleton
      // and a module-level toast id) while this subscription is scoped to one
      // version, so a solution the backend leaves stuck in PUBLISHING never
      // reaches the ACTIVE branch that would clean them up — and the toast is
      // `autoClose: false`. Leaving them raised followed the user onto every
      // solution opened afterwards: a permanent "publishing in progress" banner
      // and a view mode nobody else was going to lift.
      //
      // Conditional on having actually raised them: this cleanup runs on EVERY
      // navigation, and an unconditional version would dismiss a toast this
      // watcher does not own and force a mode over whatever the next route is
      // about to decide.
      //
      // The condition reads the STORE FLAG as well as this watcher's own ref,
      // because `PublishModal.onPublish` raises the lock and the toast
      // optimistically the moment the user confirms — before the backend has
      // moved the row to PUBLISHING, and so before this subscription has seen
      // anything. Keyed on the ref alone, a cleanup inside that window walked
      // straight past a toast that was already on screen: the same leak, just a
      // narrower window.
      const wasPublishing =
        publishInProgressForVersionIdRef.current !== null ||
        tableStore.solutionVersionPublishingInProgress;
      publishInProgressForVersionIdRef.current = null;
      tableStore.setSolutionVersionPublishingInProgress(false);
      if (wasPublishing) {
        dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
        tableStore.setState(tableStore.isViewer ? 'viewing' : 'editing');
      }
    };
  }, [solutionVersionId]);
}
